import React, { useState, useEffect } from 'react'
import { Image as ImageIcon, Download, Loader2, AlertCircle, Sparkles, Plus, GripVertical } from 'lucide-react'
import { imageApi } from '../api/client'

interface ImageHistoryItem {
  job_id: string
  prompt: string
  aspect: string
  status: 'pending' | 'ready' | 'failed'
  url?: string
  error?: string
  created: Date
}

const POLL_INTERVAL_MS = 4000
const MAX_PROMPT = 1000
const HISTORY_KEY = 'legit2clip_image_history'
const ASPECTS: { key: string; label: string; w: number; h: number }[] = [
  { key: '1:1', label: '1:1', w: 1024, h: 1024 },
  { key: '9:16', label: '9:16', w: 768, h: 1344 },
  { key: '16:9', label: '16:9', w: 1344, h: 768 },
]

export function ImagePanel({ onAddToTimeline }: { onAddToTimeline?: (clip: { url: string; text: string; duration?: number }) => void } = {}) {
  const [prompt, setPrompt] = useState('')
  const [aspect, setAspect] = useState('9:16')
  const [isGenerating, setIsGenerating] = useState(false)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [statusText, setStatusText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [history, setHistory] = useState<ImageHistoryItem[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY)
      if (raw) {
        const parsed: ImageHistoryItem[] = JSON.parse(raw).map((h: any) => ({ ...h, created: new Date(h.created) }))
        setHistory(parsed)
        const pend = parsed.find(h => h.status === 'pending')
        if (pend) setActiveJobId(pend.job_id)
      }
    } catch {}
    setLoaded(true)
  }, [])
  useEffect(() => { if (loaded) { try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history)) } catch {} } }, [history, loaded])

  useEffect(() => {
    if (!activeJobId) return
    let cancelled = false
    setIsGenerating(true)
    const poll = async () => {
      try {
        const data = await imageApi.getStatus(activeJobId)
        if (cancelled) return
        if (data.ready) {
          const url = imageApi.getDownloadUrl(activeJobId)
          setHistory(prev => prev.map(item => item.job_id === activeJobId ? { ...item, status: 'ready', url } : item))
          setIsGenerating(false); setActiveJobId(null); setStatusText('')
        } else if (data.status === 'failed' || data.status === 'cancelled') {
          const err = data.error_message || 'Generation failed'
          setHistory(prev => prev.map(item => item.job_id === activeJobId ? { ...item, status: 'failed', error: err } : item))
          setErrorMessage(err); setIsGenerating(false); setActiveJobId(null); setStatusText('')
        } else {
          const pct = typeof data.progress === 'number' && data.progress > 0 ? ` — ${Math.round(data.progress)}%` : ''
          setStatusText(`${data.status || 'processing'}${pct}`)
        }
      } catch {}
    }
    poll()
    const interval = window.setInterval(poll, POLL_INTERVAL_MS)
    return () => { cancelled = true; window.clearInterval(interval) }
  }, [activeJobId])

  const handleGenerate = async () => {
    const p = prompt.trim()
    if (!p || isGenerating) return
    setErrorMessage(''); setIsGenerating(true); setStatusText('Submitting...')
    const a = ASPECTS.find(x => x.key === aspect) || ASPECTS[0]
    try {
      const data = await imageApi.generate({ prompt: p, width: a.w, height: a.h })
      if (!data.job_id) throw new Error('No job id returned')
      setHistory(prev => [{ job_id: data.job_id, prompt: p, aspect, status: 'pending' as const, created: new Date() }, ...prev])
      setActiveJobId(data.job_id); setStatusText('Queued...')
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || error?.message || 'Failed to start generation')
      setIsGenerating(false); setStatusText('')
    }
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-600 flex-1 min-h-0 flex flex-col">
      <div className="p-3 border-b border-gray-600 shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <ImageIcon className="text-gray-400" size={16} />
          <h3 className="text-sm font-semibold text-white">Image</h3>
          <span className="text-[10px] text-gray-500">B-roll & thumbnails</span>
        </div>

        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} maxLength={MAX_PROMPT}
          placeholder="Describe the image... ('neon-lit Tokyo alley at night, cinematic, rain, shallow depth of field')" disabled={isGenerating}
          className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white resize-none placeholder-gray-500 focus:outline-none focus:border-indigo-500 disabled:opacity-50" />

        <div className="flex items-center gap-2 mt-2">
          <span className="text-[10px] text-gray-400 shrink-0">Aspect</span>
          <div className="flex items-center bg-gray-900 rounded-lg p-0.5 text-xs">
            {ASPECTS.map(a => (
              <button key={a.key} onClick={() => setAspect(a.key)} disabled={isGenerating}
                className={`px-2.5 py-1 rounded-md transition-colors ${aspect === a.key ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'} disabled:opacity-50`}>{a.label}</button>
            ))}
          </div>
        </div>

        <button onClick={handleGenerate} disabled={!prompt.trim() || isGenerating}
          className="w-full mt-2 px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {isGenerating ? (<><Loader2 className="animate-spin" size={14} />{statusText || 'Generating...'}</>) : (<><Sparkles size={14} /> Generate image</>)}
        </button>
        <p className="text-[10px] text-gray-600 mt-1 text-center">First image can take a minute while the GPU spins up.</p>

        {errorMessage && (
          <div className="flex items-start gap-1.5 mt-2 bg-red-900/40 border border-red-600/40 rounded-lg px-3 py-2">
            <AlertCircle className="text-red-400 shrink-0 mt-0.5" size={12} />
            <p className="text-xs text-red-200">{errorMessage}</p>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {history.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ImageIcon className="mx-auto mb-2" size={24} />
            <p className="text-xs">Describe a shot and generate it with AI.</p>
            <p className="text-[11px] mt-1 text-gray-600">Generated images stay here for this session.</p>
          </div>
        ) : (
          history.map((item) => (
            <div key={item.job_id} className="bg-gray-900/60 rounded-lg p-2 border border-gray-600/40">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className="text-xs text-white truncate flex-1">🖼 {item.prompt}</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[10px] text-gray-500 font-mono">{item.aspect}</span>
                  {item.status === 'pending' && (<span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-600/30 text-amber-300 flex items-center gap-1"><Loader2 className="animate-spin" size={9} />generating</span>)}
                  {item.status === 'ready' && (<span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600/30 text-green-300">ready</span>)}
                  {item.status === 'failed' && (<span className="text-[10px] px-1.5 py-0.5 rounded bg-red-600/30 text-red-300">failed</span>)}
                </div>
              </div>
              {item.status === 'pending' && (
                <div className="w-full aspect-video rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-500 text-xs gap-2"><Loader2 className="animate-spin" size={14} /> rendering…</div>
              )}
              {item.status === 'ready' && item.url && (
                <>
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={item.url} alt={item.prompt} loading="lazy" className="w-full rounded-lg border border-gray-700 bg-gray-950" />
                  </a>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <button onClick={() => item.url && onAddToTimeline?.({ url: item.url, text: item.prompt, duration: 3 })} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md bg-orange-600/80 hover:bg-orange-600 text-white text-[11px] font-medium"><Plus size={11} /> Add to video (B-roll)</button>
                    <span draggable onDragStart={(e) => { e.dataTransfer.setData('application/x-image-clip', JSON.stringify({ url: item.url, text: item.prompt, duration: 3 })); e.dataTransfer.effectAllowed = 'copy' }} title="Drag onto the B-roll track" className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-600 text-gray-400 hover:text-white text-[11px] cursor-grab active:cursor-grabbing select-none"><GripVertical size={11} /> drag</span>
                    <a href={item.url} download={`image-${item.job_id.slice(0, 8)}.png`} className="p-1 text-gray-400 hover:text-white" title="Download"><Download size={12} /></a>
                  </div>
                </>
              )}
              {item.status === 'failed' && item.error && (<p className="text-[11px] text-red-300">{item.error}</p>)}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
